/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Roboto_Mono } from "next/font/google";
import {
  DeliveredWeeklyTwoByTwo,
  DimensionEvent,
  DimensionEventStatus,
} from "@dimensional-engineering/dimensional-models";

import styles from "./WeeklyTwoByTwoHomeCTA.module.css";

import { RootState, useAppDispatch } from "@/redux/store";
import { TimeIconLarge } from "@/components/assessments-new/root/icons/TimeIcon";
import Shimmer from "@/components/shared/Shimmer/Shimmer";
import SmallProfileImage from "@/components/shared/SmallProfileImage/SmallProfileImage";
import WarningIcon from "@/components/shared/GhostBox/WarningIcon";
import { useModalContext } from "@/context/ModalContext";
import WeeklyTwoByTwoModal from "./WeeklyTwoByTwoModal";
import SadFace from "@/components/shared/GhostBox/SadFace";
import { callFirebaseFunctions } from "@/_firebase/callFirebaseFunctions";
import { setDeliveredWeeklyTwoByTwo } from "@/redux/slices/weeklyTwoByTwoSlice";

const font = Roboto_Mono({ subsets: ["latin"] });
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const WEEKLY_TWO_BY_TWO_VIEWED_SLUG_KEY = "weeklyTwoByTwoViewedSlug";

export default function WeeklyTwoByTwoHomeCTA() {
  const { setModalComponent } = useModalContext();
  const { latestChart, deliveredForLatest, weeklyTwoByTwoLoaded } = useSelector(
    (state: RootState) => state.weeklyTwoByTwo
  );
  const imageUrl = useSelector((state: RootState) => state.user.imageURL);
  const publicProfile = useSelector(
    (state: RootState) => state.user.publicProfileData
  );
  const dimensionEvents = useSelector(
    (state: RootState) => state.user.dimensionEvents
  );

  const [timeLeft, setTimeLeft] = useState<string>("0d:0h:0m:0s");
  const hasCompletedCognition = useMemo(
    () => checkHasCompletedCognition(dimensionEvents),
    [dimensionEvents]
  );

  const xPercent = useMemo(() => {
    if (!deliveredForLatest) {
      return 50;
    }
    return mapLlmPositionToPercent(deliveredForLatest.xPosition);
  }, [deliveredForLatest]);

  const yPercent = useMemo(() => {
    if (!deliveredForLatest) {
      return 50;
    }
    return mapLlmPositionToPercent(deliveredForLatest.yPosition);
  }, [deliveredForLatest]);

  useEffect(() => {
    if (!deliveredForLatest?.deliveredOn) {
      setTimeLeft("0d:0h:0m:0s");
      return;
    }

    const updateTimer = () => {
      const expiry = deliveredForLatest.deliveredOn + ONE_WEEK_MS;
      const now = Date.now();
      const remainingSeconds = Math.max(Math.floor((expiry - now) / 1000), 0);
      setTimeLeft(formatCountdown(remainingSeconds));
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, [deliveredForLatest?.deliveredOn]);

  if (!weeklyTwoByTwoLoaded) {
    return (
      <section className={`${styles.main} ${styles.loading}`}>
        <Shimmer />
      </section>
    );
  }

  if (!latestChart) {
    return (
      <NoChartAvailableCard hasCompletedCognition={hasCompletedCognition} />
    );
  }

  if (!deliveredForLatest) {
    return (
      <NoChartAvailableCard hasCompletedCognition={hasCompletedCognition} />
    );
  }

  const viewedSlug = localStorage.getItem(WEEKLY_TWO_BY_TWO_VIEWED_SLUG_KEY);
  const isViewed = viewedSlug === latestChart.slug;

  return (
    <section
      onClick={() => {
        localStorage.setItem(
          WEEKLY_TWO_BY_TWO_VIEWED_SLUG_KEY,
          latestChart.slug
        );
        setModalComponent(<WeeklyTwoByTwoModal />);
      }}
      className={styles.main}
      style={isViewed ? {} : { border: "1px solid #2CB0FF" }}
    >
      <h4 className={`${styles.heading} ${font.className}`}>
        {latestChart.chartName.toUpperCase()}
      </h4>
      <div className={styles.chart}>
        <div className={styles.verticalLine}></div>
        <div className={styles.horizontalLine}></div>

        <p className={styles.topLabel}>{latestChart.topAxisLabel}</p>
        <p className={styles.bottomLabel}>{latestChart.bottomAxisLabel}</p>
        <p className={styles.leftLabel}>{latestChart.leftAxisLabel}</p>
        <p className={styles.rightLabel}>{latestChart.rightAxisLabel}</p>

        <div
          style={{
            left: `calc(${xPercent}% - 26px)`,
            bottom: `calc(${yPercent}% - 26px)`,
          }}
          className={styles.avatarPin}
        >
          <SmallProfileImage
            noLink
            medium
            imageUrl={imageUrl || "/default-image.png"}
            publicProfile={publicProfile}
          />
        </div>
      </div>
      <div className={styles.bottomBar}>
        <div className={styles.timeGroup}>
          <TimeIconLarge />
          <p className={styles.timeText}>{timeLeft}</p>
        </div>
      </div>
    </section>
  );
}

function NoChartAvailableCard(props: { hasCompletedCognition: boolean }) {
  const dispatch = useAppDispatch();
  const [retrying, setRetrying] = useState(false);
  const message = props.hasCompletedCognition
    ? "Failed to generate your results. Tap to retry."
    : "No chart available today. Complete more Dimensions to unlock more charts.";
  const handleRetry = async () => {
    if (retrying) {
      return;
    }

    setRetrying(true);
    try {
      const retry = callFirebaseFunctions("computeMemberLatestWeeklyTwoByTwo");
      const result = await retry();
      dispatch(
        setDeliveredWeeklyTwoByTwo(
          result.data as DeliveredWeeklyTwoByTwo | null
        )
      );
    } catch (error) {
      console.error(error);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <section
      style={{ maxHeight: "350px", minHeight: 0 }}
      className={styles.main}
    >
      <h4
        className={`${styles.heading} ${styles.unavailableHeading} ${font.className}`}
      >
        CHART
      </h4>
      <div className={styles.unavailableBody}>
        <div className={styles.unavailableIcon}>
          <div className={styles.unavailableVertical}></div>
          <div className={styles.unavailableHorizontal}></div>
          <div className={styles.iconBlueDot}></div>
          <div className={styles.iconPinkDot}></div>
        </div>
        <div className={styles.ghostBox}>
          <div
            style={{
              padding: !props.hasCompletedCognition ? "8px 7px 9px 7px" : 0,
            }}
            className={styles.warningIconWrap}
          >
            {!props.hasCompletedCognition ? (
              <WarningIcon active />
            ) : (
              <SadFace />
            )}
          </div>
          <p className={styles.ghostMessage}>{message}</p>
          {props.hasCompletedCognition && (
            <button
              type="button"
              className={styles.retryButton}
              disabled={retrying}
              onClick={(event) => {
                event.stopPropagation();
                void handleRetry();
              }}
            >
              {retrying ? (
                <span className={styles.retrySpinner} aria-label="Retrying" />
              ) : (
                "Retry"
              )}
            </button>
          )}
        </div>
      </div>
      <div className={styles.bottomBar}>
        <div className={styles.timeGroup}>
          <TimeIconLarge />
          <p className={styles.timeText}>Next chart available soon!</p>
        </div>
      </div>
    </section>
  );
}

function checkHasCompletedCognition(
  dimensionEvents: DimensionEvent[] | null
): boolean {
  const cognitionEvent = dimensionEvents?.find(
    (event) => event.dimensionSlug === "personality-II"
  );

  if (!cognitionEvent) {
    return false;
  }

  if (
    cognitionEvent.event === DimensionEventStatus.complete ||
    cognitionEvent.event === DimensionEventStatus.reset
  ) {
    return true;
  }

  if (
    cognitionEvent.event !== DimensionEventStatus.deferred &&
    cognitionEvent.event !== DimensionEventStatus.deferredGated
  ) {
    return false;
  }

  const cognitionLongEvent = dimensionEvents?.find(
    (event) => event.dimensionSlug === "cognition-long"
  );

  if (!cognitionLongEvent) {
    return cognitionEvent.event === DimensionEventStatus.deferred;
  }

  if (cognitionLongEvent.event === DimensionEventStatus.started) {
    return cognitionEvent.event === DimensionEventStatus.deferred;
  }

  return cognitionLongEvent.event !== DimensionEventStatus.resetGated;
}

function formatCountdown(seconds: number) {
  const days = Math.floor(seconds / (24 * 3600));
  const hours = Math.floor((seconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${days}d:${hours}h:${minutes}m:${remainingSeconds}s`;
}

function mapLlmPositionToPercent(position: number) {
  // LLM gives chart coordinates in [-100, 100], matching DeliveredWeeklyTwoByTwo.
  const normalized = (position + 100) / 200;
  return normalized * 100;
}
